import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { generateAppLink, isNativePlatform } from "@/utils/deep-links";
import { trackRegistrationComplete } from "./meta-events";
import { analytics } from "./analytics";

export type AuthError = {
  message: string;
};

export interface AuthResult {
  user: any | null;
  error: string | null;
}

export const signUp = async (email: string, password: string): Promise<AuthResult> => {
  console.log('Auth service: Starting signUp process for email:', email);
  
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });

    if (error) {
      console.error('Auth service: Signup error:', error.message);
      toast.error("Erro ao criar conta", {
        description: error.message,
      });
      return { user: null, error: error.message };
    }

    console.log('Auth service: Signup successful, user ID:', data.user?.id);
    console.log('Auth service: Email verification status:', data.user?.email_confirmed_at ? 'Confirmed' : 'Pending');
    console.log('Auth service: Auth session after signup:', !!data.session);
    
    // Show success toast
    toast.success("Conta criada com sucesso!");
    
    // Show email verification toast if email is not confirmed
    if (!data.user?.email_confirmed_at) {
      toast.info("Verifique seu e-mail para confirmar sua conta.");
    }
    
    // Track successful registration with Meta Conversions API
    if (data.user) {
      try {
        await trackRegistrationComplete(email);
      } catch (metaError) {
        // Fail silently - we don't want to block user flow if tracking fails
        console.error('Failed to track registration event:', metaError);
      }
      
      // 🎯 NEW: Track with Mixpanel (parallel to Meta)
      try {
        analytics.track('Sign Up', {
          email: email,
          signup_method: 'email',
          source: 'web',
          user_id: data.user.id
        });
        console.log('✅ Mixpanel Sign Up event tracked');
      } catch (mixpanelError) {
        // Fail silently - we don't want to block user flow if tracking fails
        console.error('Failed to track Mixpanel signup event:', mixpanelError);
      }
    }
    
    return { user: data.user, error: null };
  } catch (error) {
    console.error('Auth service: Unexpected error during signup:', error);
    const e = error as Error;
    toast.error("Erro ao criar conta", {
      description: e.message,
    });
    return { user: null, error: e.message };
  }
};

export const signIn = async (email: string, password: string): Promise<AuthResult> => {
  console.log('Auth service: Starting signIn process for email:', email);
  
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Auth service: Login error:', error.message);
      toast.error("Erro ao fazer login", {
        description: error.message,
      });
      return { user: null, error: error.message };
    }

    console.log('Auth service: Login successful, user ID:', data.user?.id);
    return { user: data.user, error: null };
  } catch (error) {
    console.error('Auth service: Unexpected error during login:', error);
    const e = error as Error;
    toast.error("Erro ao fazer login", {
      description: e.message,
    });
    return { user: null, error: e.message };
  }
};

export const signOut = async (): Promise<{ error: string | null }> => {
  console.log('Auth service: Starting signOut process');
  
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('Auth service: Signout error:', error.message);
      toast.error("Erro ao sair", {
        description: error.message,
      });
      return { error: error.message };
    }
    
    console.log('Auth service: Signout successful');
    return { error: null };
  } catch (error) {
    console.error('Auth service: Unexpected error during signout:', error);
    const e = error as Error;
    toast.error("Erro ao sair", {
      description: e.message,
    });
    return { error: e.message };
  }
};

export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      console.error("Error getting current user:", error);
      return { user: null, error: error.message };
    }

    return { user: data.user, error: null };
  } catch (error) {
    const e = error as Error;
    console.error("Unexpected error in getCurrentUser:", e);
    return { user: null, error: e.message };
  }
};

export const setupAuthListener = (callback: (user: any) => void) => {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    console.log("Auth state changed:", event);
    callback(session?.user || null);
  });
  
  return data.subscription;
};

export const resetPassword = async (email: string): Promise<{ error: string | null }> => {
  console.log('Auth service: Starting resetPassword process for email:', email);
  
  try {
    // Determine the redirect URL based on platform (web vs native)
    // For native app, we want to use deep links to bring users back to the app
    let redirectTo: string;
    
    if (isNativePlatform()) {
      // If in the app, we'll use our app URL scheme to redirect back to app
      redirectTo = 'pulsefiction://reset-password-confirm';
    } else {
      // For web, we'll generate a universal link that can open the app if installed
      // or fall back to the web version
      redirectTo = generateAppLink('/reset-password-confirm');
    }
    
    console.log('Auth service: Using redirectTo:', redirectTo);
    
    // Call Supabase to initiate the password reset process
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectTo,
    });

    if (error) {
      console.error('Auth service: Reset password error:', error.message);
      toast.error("Erro ao enviar email de recuperação", {
        description: error.message,
      });
      return { error: error.message };
    }

    console.log('Auth service: Reset password email sent successfully');
    return { error: null };
  } catch (error) {
    console.error('Auth service: Unexpected error during reset password:', error);
    const e = error as Error;
    toast.error("Erro ao enviar email de recuperação", {
      description: e.message,
    });
    return { error: e.message };
  }
};
